import {gql} from 'graphql-request';
import {useGqlRequest} from '@/api/request';
import {
  AnalysisItem,
  CategoryHallSales,
  KodataCategoryGroup,
} from '@/map/@types';
import {AreaOverlayType} from '@/map/enums';
import {useQuery} from '@tanstack/react-query';
import {getAreaQueryVariables} from './variables';

export const useStoreHallSalesCategoy = ({
  item,
  categories,
}: {
  item: AnalysisItem;
  categories?: KodataCategoryGroup[];
}) => {
  const query =
    item.type === AreaOverlayType.CIRCLE
      ? QUERY_STORE_HALL_SALES_CATEGORY_BY_POINT
      : item.type === AreaOverlayType.POLYGON
      ? QUERY_STORE_HALL_SALES_CATEGORY_BY_POLYGON
      : QUERY_STORE_HALL_SALES_CATEGORY_BY_HJD;

  const id = item?.id;
  const enabled = !!id && !!categories;

  const request = useGqlRequest({
    query,
  });

  const queryFn = ():
    | undefined
    | Promise<{
        result?: CategoryHallSales[];
      }> => {
    if (!enabled) {
      return;
    }

    const variant = {
      categories,
      ...getAreaQueryVariables(item),
    };

    return request(variant);
  };

  const select = (data?: {result?: CategoryHallSales[]}) => data?.result;

  return useQuery({
    queryKey: ['map', 'analytics', id, 'hall-sales-category', categories],
    enabled,
    queryFn,
    select,
  });
};

const hallSalesSubfields = `
  averageSales
  increaseRate
  storeCount
  totalSales
`;

const QUERY_STORE_HALL_SALES_CATEGORY_BY_POINT = gql`
  query hallSalesByPoint($categories: [Category!]!, $lat: Float!, $lng: Float!, $radius: Float!) {
    result: hallSalesByPoint(categories: $categories, lat: $lat, lng: $lng, radius: $radius) {
      ${hallSalesSubfields}
    }
  }
`;

const QUERY_STORE_HALL_SALES_CATEGORY_BY_POLYGON = gql`
  query hallSalesByPolygon($categories: [Category!]!, $polygon: GeoPolygonArgs!) {
    result: hallSalesByPolygon(categories: $categories, polygon: $polygon) {
      ${hallSalesSubfields}
    }
  }
`;

const QUERY_STORE_HALL_SALES_CATEGORY_BY_HJD = gql`
  query hallSalesByHjd($admCode: String!, $categories: [Category!]!) {
    result: hallSalesByHjd(admCode: $admCode, categories: $categories) {
      ${hallSalesSubfields}
    }
  }
`;
